import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TCompanyUserStatus } from 'librechat-data-provider';
import { useCompanyGroupsQuery, useCompanyPolicyQuery } from './hooks';
import Sidebar, { type LeadHRScreen } from './Sidebar';
import { Button, FilterSelect, Muted } from './Chrome';
import Analytics from './Analytics';
import Settings from './Settings';
import Invite from './Invite';
import Users from './Users';
import './theme.css';

export interface LeadHRDashboardProps {
  /** Signs out and returns to the login screen. */
  onSignOut: () => void;
  userName?: string;
}

/**
 * The Lead HR company dashboard.
 *
 * Screen selection is local state rather than routing: the host owns the URL, and the
 * screens read the same company. A Lead HR in exactly one company never sees a picker.
 */
export default function LeadHRDashboard({
  onSignOut,
  userName,
}: LeadHRDashboardProps): ReactElement {
  const { t } = useTranslation();
  const [screen, setScreen] = useState<LeadHRScreen>('analytics');
  const [selectedId, setSelectedId] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  /** Lets another screen open Licences already narrowed to one status. */
  const [usersStatus, setUsersStatus] = useState<TCompanyUserStatus | ''>('');

  const { data, isLoading } = useCompanyGroupsQuery(true);
  const groups = data?.groups ?? [];
  const groupId = selectedId || (groups.length > 0 ? (groups[0]?._id ?? '') : '');
  const { data: policy } = useCompanyPolicyQuery(groupId);

  const navigate = (next: LeadHRScreen, status: TCompanyUserStatus | '' = '') => {
    setUsersStatus(status);
    setScreen(next);
  };

  const renderScreen = () => {
    if (isLoading) {
      return <Muted>{t('com_ui_leadhr_loading')}</Muted>;
    }
    if (groups.length === 0) {
      return <Muted>{t('com_ui_leadhr_no_companies')}</Muted>;
    }
    if (screen === 'analytics') {
      return (
        <Analytics
          groupId={groupId}
          onInvite={() => setShowInvite(true)}
          onViewInvitations={() => navigate('users', 'invited')}
        />
      );
    }
    if (screen === 'settings') {
      return <Settings groupId={groupId} />;
    }
    return (
      <Users key={usersStatus} groupId={groupId} policy={policy} initialStatus={usersStatus} />
    );
  };

  return (
    <div className="leadhr flex min-h-screen flex-col items-stretch md:flex-row md:items-start">
      <Sidebar
        active={screen}
        onNavigate={navigate}
        onSignOut={onSignOut}
        companyName={policy?.groupName}
        userName={userName}
      />
      <main className="flex min-w-0 max-w-[1160px] flex-1 flex-col gap-10 px-5 pb-16 pt-6">
        <div className="-mb-4 flex flex-wrap items-end justify-end gap-4">
          {groups.length > 1 ? (
            <div className="mr-auto">
              <FilterSelect
                id="leadhr-company"
                label={t('com_ui_leadhr_select_company')}
                value={groupId}
                options={groups.map((group) => ({ value: group._id, label: group.name }))}
                onChange={setSelectedId}
              />
            </div>
          ) : null}
          <Button onClick={() => navigate('users')}>{t('com_ui_leadhr_manage_licences')}</Button>
          <Button variant="primary" icon disabled={!groupId} onClick={() => setShowInvite(true)}>
            {t('com_ui_leadhr_invite_users')}
          </Button>
        </div>
        {renderScreen()}
      </main>
      {showInvite && groupId ? (
        <Invite groupId={groupId} onClose={() => setShowInvite(false)} />
      ) : null}
    </div>
  );
}